import { NoData } from "@/components/common/section-components/NoData";
import { SamplingRequestActions } from "@/components/common/section-components/action/SamplingRequestActions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SamplingRequestFilter } from "@/components/common/section-components/filters/SamplingRequestFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { CategoriaServicio, EntityTypes } from "@/constants/enums";
import { EstadosSolicitudVoleo } from "@/constants/enums/status/entity-status-ids";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearVoleoFiltersHandler,
  fetchVoleoRequestsHandler,
  submitVoleoFiltersHandler,
  VOLEO_REQUESTS_PAGE_SIZE,
} from "./schema/handler-functions";
import { voleoRequestsHeaderElements } from "./schema/header-elements";
import {
  VoleoRequestListItem,
  VoleoRequestsListResponse,
} from "./schema/voleo-request.schema";

export const VoleoRequestsPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const handleAdd = () => {
    navigate(AppRoutes.VOLEO_REQUEST_CREATION);
  };

  const [isActive, setIsActive] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<VoleoRequestsListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);
  const defaultStatusFilters = [EstadosSolicitudVoleo.Pendiente];

  const { control, handleSubmit, setValue, getValues } = useForm<FieldValues>({
    defaultValues: {
      estados: defaultStatusFilters,
    },
  });
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchVoleoRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      setValue("estados", defaultStatusFilters);
      submitVoleoFiltersHandler({
        event: getValues(),
        setPage,
        fetchList,
        setCurrentFilters,
        setFiltersApplied,
        setOpenFilters,
        filtersEmpty: false,
      });
    }
  }, [getValues, isActive, setValue]);

  const submitFilters = async (event: FieldValues) => {
    await submitVoleoFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearVoleoFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(id)));
  };

  const getActionElements = (item: VoleoRequestListItem) => (
    <SamplingRequestActions handleDetail={handleDetail} item={item} />
  );

  const getDisplayEstablecimiento = (el: VoleoRequestListItem) => {
    return `${el?.establecimiento ?? "-"} - ${new Intl.NumberFormat("es-AR").format(el?.hectareas ?? 0)} Ha`;
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Servicio de voleo"
        addButtonLabel="Agregar"
        addButtonType="button"
        handleAdd={handleAdd}
        loading={loading}
        showButtons={hasWriteAccess(PermissionCode.BROADCAST_REQUEST)}
      />

      <SamplingRequestFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        serviceCategory={CategoriaServicio.Voleo}
        statusEntityType={EntityTypes.SolicitudVoleo}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: VoleoRequestListItem) => ({
                  ...el,
                  displayEstablecimiento: getDisplayEstablecimiento(el),
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estado as unknown as ServiceRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={voleoRequestsHeaderElements}
              actionElements={getActionElements}
            />
            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={VOLEO_REQUESTS_PAGE_SIZE}
                count={list?.totalRecords ?? VOLEO_REQUESTS_PAGE_SIZE}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
